import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  ElementRef,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { trigger, state, style, transition, animate } from '@angular/animations';

/**
 * Info tooltip component - displays onboarding/info content
 * with a dialog-like appearance and close button
 */
@Component({
  selector: 'app-info-tooltip',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0, transform: 'scale(0.95)' })),
      state('*', style({ opacity: 1, transform: 'scale(1)' })),
      transition('void => *', animate('200ms ease-out')),
      transition('* => void', animate('150ms ease-in')),
    ]),
  ],
})
export class InfoTooltipComponent implements AfterViewInit {
  @Input() content?: string; // Simple text content
  @Input() template?: TemplateRef<any>; // HTML template
  @Input() direction: 'up' | 'down' | 'left' | 'right' = 'up';
  @Input() tooltipAlign: 'auto' | number | string = 'auto';
  @Input() targetElement?: HTMLElement; // Element being highlighted
  @Input() highlightElement = true; // Whether to highlight the target
  @Input() backdropEnabled = true; // Whether backdrop is shown (dark background)
  @Input() dismissOnClick = false; // Allow click anywhere to close
  @Input() showCloseButton = true; // Show close button
  @Input() arrowXOffset?: number | string; // Manual X offset percent (overrides calculation)
  @Input() arrowYOffset?: number | string; // Manual Y offset percent (overrides calculation)

  @ViewChild('tooltipContent', { static: false })
  tooltipContentRef?: ElementRef<HTMLElement>;

  arrowOffsetXPercent?: string;
  arrowOffsetYPercent?: string;

  @Output() close = new EventEmitter<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Handle close button click
   */
  onClose() {
    this.close.emit();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateArrowOffset();
      this.cdr.detectChanges();
    }, 100);
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateArrowOffset();
    this.cdr.detectChanges();
  }

  /**
   * Handle click on tooltip container
   */
  onContainerClick() {
    if (this.dismissOnClick) {
      this.close.emit();
    }
  }

  private updateArrowOffset() {
    const tooltipEl = this.tooltipContentRef?.nativeElement;
    if (!tooltipEl || !this.targetElement) {
      return;
    }

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const targetRect = this.targetElement.getBoundingClientRect();
    const alignPercent = this.getAlignPercent();
    const manualX = this.getManualPercent(this.arrowXOffset);
    const manualY = this.getManualPercent(this.arrowYOffset);

    if (this.direction === 'up' || this.direction === 'down') {
      const percent = manualX ?? alignPercent ?? this.getTargetPercentX(tooltipRect, targetRect);
      this.arrowOffsetXPercent = `${this.clampPercent(percent)}%`;
      this.arrowOffsetYPercent = undefined;
      tooltipEl.style.setProperty('--arrow-offset-x', this.arrowOffsetXPercent);
      tooltipEl.style.removeProperty('--arrow-offset-y');
      return;
    }

    if (this.direction === 'left' || this.direction === 'right') {
      const percent = manualY ?? alignPercent ?? this.getTargetPercentY(tooltipRect, targetRect);
      this.arrowOffsetYPercent = `${this.clampPercent(percent)}%`;
      this.arrowOffsetXPercent = undefined;
      tooltipEl.style.setProperty('--arrow-offset-y', this.arrowOffsetYPercent);
      tooltipEl.style.removeProperty('--arrow-offset-x');
    }
  }

  private getAlignPercent(): number | undefined {
    if (this.tooltipAlign === 'auto') {
      return undefined;
    }

    if (typeof this.tooltipAlign === 'number') {
      return this.tooltipAlign;
    }

    const trimmed = `${this.tooltipAlign}`.trim();
    if (trimmed.endsWith('%')) {
      const value = parseFloat(trimmed.replace('%', ''));
      return Number.isFinite(value) ? value : undefined;
    }

    const value = parseFloat(trimmed);
    return Number.isFinite(value) ? value : undefined;
  }

  private getManualPercent(value?: number | string): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'number') {
      return value;
    }
    const trimmed = `${value}`.trim();
    if (trimmed.endsWith('%')) {
      const parsed = parseFloat(trimmed.replace('%', ''));
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    const parsed = parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getTargetPercentX(tooltipRect: DOMRect, targetRect: DOMRect): number {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const offsetX = targetCenterX - tooltipRect.left;
    return (offsetX / tooltipRect.width) * 100;
  }

  private getTargetPercentY(tooltipRect: DOMRect, targetRect: DOMRect): number {
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const offsetY = targetCenterY - tooltipRect.top;
    return (offsetY / tooltipRect.height) * 100;
  }

  private clampPercent(value: number): number {
    return Math.min(100, Math.max(0, value));
  }

}
